import cx from "classnames";

import { Card, Link as DSLink, Heading } from "@icg360/design-system";

import { Body } from "components/common/body";
import { Stack } from "components/common/stack";
import { PublicHeader } from "components/public-header";
import { PageFooter } from "components/shared/page-footer";

import styles from "./terms.module.scss";

const Terms = () => {
  return (
    <>
      <PublicHeader hideNav />
      <div className={styles.termsContainer}>
        <div className={styles.termsCard}>
          <Card title="MySageSure Terms of Use">
            <Stack>
              <div className={styles.welcome}>
                <Body>
                  Welcome to MySageSure! When you use MySageSure, you are
                  agreeing to our Terms of Use. Please review this Terms of Use
                  carefully.
                </Body>
                <Body className={styles.revisedDate}>
                  Last Revised: 06/30/2025
                </Body>
              </div>
              <div className={styles.termsSection}>
                <p>
                  MySageSure is provided by SageSure Insurance Managers and its
                  affiliates, Insight Catastrophe Managers and Insight
                  Catastrophe Group (collectively, “SageSure,” “us,” or “we”).
                  This Terms of Use governs your access to and use of the
                  MySageSure online portal and payment applications
                  (&quot;MySageSure&quot;).
                </p>
                <p>
                  By using MySageSure, you signify your agreement to this Terms
                  of Use and our{" "}
                  <span className={styles.linkWrapper}>
                    <DSLink
                      href="/privacy-policy/"
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Privacy Policy
                    </DSLink>
                    .
                  </span>{" "}
                  If you do not agree to this Terms of Use and our Privacy
                  Policy, you may not use MySageSure. Please note that this
                  Terms of Use does not apply to use of any of our offerings or
                  services other than MySageSure. Your use of any other of our
                  offerings or services will be subject to an additional terms
                  of use and privacy policy, as applicable.
                </p>
              </div>
              <Stack>
                <Heading size="sm">
                  <span className={styles.sectionNumber}>1.</span>
                  Provision of MySageSure
                </Heading>
                <div className={styles.termsSection}>
                  <p>
                    MySageSure is provided for the use of our customers free of
                    charge, though fees may exist for specific functions or
                    services. Should charges be introduced by SageSure in the
                    future, you will be notified in advance and you will have an
                    opportunity to discontinue your use of MySageSure.
                  </p>
                  <p>
                    SageSure reserves the right to change or modify MySageSure,
                    suspend or terminate MySageSure, or suspend or terminate
                    your access to and use of MySageSure, at any time and for
                    any reason.
                  </p>
                  <p>
                    Although MySageSure is accessible worldwide, it is not
                    necessarily appropriate for use outside the United States.
                    MySageSure and your registration are void where prohibited.
                    If you choose to access MySageSure from outside the United
                    States, you do so on your own initiative and you are solely
                    responsible for complying with applicable local laws.
                  </p>
                </div>
              </Stack>
              <Stack>
                <Heading size="sm">
                  <span className={styles.sectionNumber}>2.</span>
                  Requirements for Using MySageSure
                </Heading>
                <ol className={cx(styles.termsSection, styles.alphaList)}>
                  <li>
                    <b>a. Account Security.</b> Only registered users may access
                    and use MySageSure. You must select a user name and password
                    to access MySageSure. You must keep that username and
                    password confidential and not share your username or
                    password with any other person. Please note that if you
                    share your username and password with another person, that
                    person will be able to access your personal information. If
                    you believe your password has been compromised or taken
                    without your permission, please change to a new password by
                    following the instructions described in MySageSure.
                  </li>
                  <li>
                    <b>b. Permitted Uses/Prohibited Actions.</b> You are not
                    permitted to use MySageSure other than for private and
                    non-commercial purposes. The use of any automated system or
                    software to extract data from MySageSure, including scraper
                    bots, is expressly prohibited. You may not use MySageSure to
                    engage in any illegal activity. You may not use MySageSure
                    to engage in conduct which is defamatory, libelous,
                    threatening or harassing or that infringes on a third
                    party&apos;s intellectual property or other proprietary
                    rights. In the event you engage in activity that is illegal,
                    defamatory, libelous, threatening, or harassing, SageSure
                    reserves the right to disclose any information you provide
                    through the use of MySageSure or other SageSure owned
                    electronic sites or applications to law enforcement
                    authorities as deemed appropriate in the sole discretion of
                    SageSure. You agree that any information you provide through
                    MySageSure will be truthful and accurate. SageSure reserves
                    the right to take such action as it deems necessary to
                    enforce this prohibition, including legal action, without
                    prior formal notice.
                  </li>
                  <li>
                    <b>c. Unauthorized Access.</b> Unauthorized use of or access
                    to MySageSure is prohibited. Actual or attempted
                    unauthorized use of or access to MySageSure may result in
                    criminal and/or civil prosecution. Attempts to access
                    MySageSure or information contained within without
                    authorization may be viewed, monitored and recorded and any
                    information obtained may be given to law enforcement
                    organizations in connection with any investigation or
                    prosecution of possible criminal activity on MySageSure. If
                    you are not an authorized user of MySageSure or do not
                    consent to continued monitoring, you should not attempt to
                    access such areas.
                  </li>
                </ol>
              </Stack>
              <Stack>
                <Heading size="sm">
                  <span className={styles.sectionNumber}>3.</span>
                  Terms and Conditions
                </Heading>
                <ol className={cx(styles.termsSection, styles.alphaList)}>
                  <li className={styles.agentInfo}>
                    <b>a. Intellectual Property and Copyright.</b> All content
                    included on MySageSure, including but not limited to text,
                    photographs, graphics, icon buttons, images, artwork, names,
                    logos, trademarks, service marks and data (“Content”) in any
                    form including compilations, are protected by U.S. and
                    international copyright laws. Direct or indirect
                    reproduction of the Content in whole or in part by any means
                    is prohibited without the express written consent of
                    SageSure; except that you may print out a single copy of
                    pages on MySageSure for your personal, non-commercial use
                    provided that all copyright, trademark or other notices are
                    retained. ALL OTHER USES ARE PROHIBITED. Pursuant to 17
                    U.S.C. 512(c), notice is hereby given that the person
                    identified below is the Designated Agent for receiving
                    complaints concerning MySageSure, including for purposes of
                    receiving notification of any claims of intellectual
                    property infringement:
                    <ul>
                      <li>Name: Lisa Bernstein, SageSure Insurance Managers</li>
                      <li>
                        Mailing Address: 101 Hudson Street, Suite 2700, Jersey
                        City, NJ 07302
                      </li>
                      <li>Telephone: (212) 207-3399</li>
                    </ul>
                  </li>
                  <li>
                    <b>b. Security.</b> SageSure maintains administrative
                    policies and technical safeguards that are designed to
                    protect your information against loss or unauthorized access
                    or acquisition. While we work to protect the security of
                    your account and information, SageSure cannot guarantee that
                    unauthorized third parties will not be able to defeat our
                    security measures. You acknowledge that when you use
                    MySageSure, information may be transmitted over a medium
                    that is beyond the control of SageSure and its contractors.
                    Please notify us immediately of any compromise or
                    unauthorized use of your account. YOU HEREBY EXPRESSLY
                    ASSUME THE SOLE RISK OF ANY UNAUTHORIZED INTENTIONAL
                    INTRUSION OR ACCESS, OR OF ANY DELAY, FAILURE, INTERRUPTION
                    OR CORRUPTION OF DATA OR OTHER INFORMATION TRANSMITTED IN
                    CONNECTION WITH THE USE OF MYSAGESURE.
                  </li>
                  <li>
                    <b>c. Links to Third Party Sites.</b> MySageSure may contain
                    links to websites operated by third parties. Any such links
                    are provided for your convenience only. SageSure does not
                    control such web sites, and is not responsible for the
                    content or performance of those sites. The inclusion of
                    links to other websites does not imply any endorsement of
                    the material on the websites or the operators of the website
                    or any association with the operators. Accessing and using
                    other websites is strictly at your own risk. You are
                    responsible for viewing and abiding by the terms and
                    conditions of use and the privacy statements of the other
                    websites.
                  </li>
                  <li>
                    <b>d. DISCLAIMERS.</b> MYSAGESURE IS PROVIDED BY SAGESURE ON
                    AN “AS IS” BASIS. SAGESURE MAKES NO REPRESENTATIONS OR
                    WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, AS TO THE
                    OPERATION OF MYSAGESURE, OR THE CONTENT, PRODUCTS, OR
                    SERVICES INCLUDED THEREIN. SAGESURE DOES NOT WARRANT THAT
                    MYSAGESURE WILL BE UNINTERRUPTED OR ERROR FREE. SAGESURE
                    DOES NOT GUARANTEE THAT YOU WILL BE ABLE TO ACCESS
                    MYSAGESURE AT THE TIME OF YOUR CHOOSING. TO THE FULLEST
                    EXTENT PERMISSIBLE BY APPLICABLE LAW, SAGESURE DISCLAIMS ALL
                    WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO
                    IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
                    PARTICULAR PURPOSE, TITLE, AND/OR INFRINGEMENT.
                  </li>
                  <li>
                    <b>e. Arbitration.</b> PLEASE READ THIS ARBITRATION CLAUSE
                    CAREFULLY. It affects your legal rights. By agreeing to
                    these Terms of Use or by accessing or using this website,
                    you agree to the following arbitration terms for resolution
                    of certain disputes: Any dispute or claim relating in any
                    way to your use of MySageSure or services offered through
                    the website will be resolved by binding arbitration, rather
                    than in court. However, you may bring qualifying claims in
                    small claims court. This Agreement does not apply to claims
                    involving criminal conduct, or claims arising under federal,
                    state or local statutes that expressly prohibit arbitration.
                    To initiate arbitration, you must send a written request
                    describing your claim to SageSure Insurance Managers LLC at
                    the location listed in the “Contact Information” section
                    above. The arbitration will be administered by the American
                    Arbitration Association (“AAA”) in accordance with its
                    Consumer Arbitration Rules, including the Supplementary
                    Procedures for Consumer-Related Disputes which are available
                    at{" "}
                    <DSLink
                      href="https://adr.org/"
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      www.adr.org
                    </DSLink>{" "}
                    or by calling 1-800-778-7879. The applicable administrative
                    fees and costs will be governed by the AAA&apos;s Consumer
                    Arbitration Fee Schedule, also available on the AAA&apos;s
                    website. You may choose to have the arbitration conducted by
                    telephone, based on written submissions, or in person in the
                    county where you live or at another mutually agreed
                    location. The arbitrator&apos;s decision will be based on
                    applicable law and evidence and shall include a written
                    explanation. The arbitrator&apos;s decision is final and
                    binding.
                  </li>
                  <li>
                    <b>f. Governing Law/Severability.</b> This Terms of Use
                    shall be governed by and interpreted according to the laws
                    of the State of New York without respect to any conflict of
                    laws provisions. If any portion of this Terms of Use is
                    unlawful, void or unenforceable, it shall not affect the
                    validity or enforceability of any other provision.
                  </li>
                </ol>
              </Stack>
              <Stack>
                <Heading size="sm">Contact Us</Heading>
                <Body className={styles.termsSection}>
                  Questions? You may contact us at{" "}
                  <DSLink href="mailto:marketing@sagesure.com">
                    marketing@sagesure.com
                  </DSLink>
                  .
                </Body>
              </Stack>
            </Stack>
          </Card>
        </div>
      </div>
      <PageFooter unauthenticated />
    </>
  );
};

export default Terms;
